import { gzipSync, gunzipSync } from "fflate";

/**
 * Threshold for compression in bytes (50KB)
 * Content larger than this will be automatically compressed
 */
const COMPRESSION_THRESHOLD = 50 * 1024; // 50KB

/**
 * Marker to identify compressed content in the payload
 */
export const COMPRESSION_MARKER = "__VIA_COMPRESSED__";

/**
 * Compress a string using gzip compression
 * @param data - The string data to compress
 * @returns Base64 encoded compressed data
 */
export const compressString = (data: string): string => {
  try {
    // Convert string to Uint8Array
    const uint8Array = new TextEncoder().encode(data);

    // Compress using fflate (gzip)
    const compressed = gzipSync(uint8Array);

    // Convert to base64 for safe JSON transmission
    // Use Buffer if available (Node.js/test env), otherwise use btoa (browser)
    let base64: string;

    if (typeof Buffer !== "undefined") {
      // Node.js environment (including tests)
      base64 = Buffer.from(compressed).toString("base64");
    } else {
      // Browser environment
      let binary = "";
      for (let i = 0; i < compressed.length; i++) {
        binary += String.fromCharCode(compressed[i]);
      }
      base64 = btoa(binary);
    }

    return base64;
  } catch (error) {
    console.error("Error compressing string:", error);
    throw new Error("Failed to compress data");
  }
};

/**
 * Decompress a base64 encoded gzip compressed string
 * @param compressedData - Base64 encoded compressed data
 * @returns Original decompressed string
 */
export const decompressString = (compressedData: string): string => {
  try {
    // Convert base64 to Uint8Array
    // Use Buffer if available (Node.js/test env), otherwise use atob (browser)
    let uint8Array: Uint8Array;

    if (typeof Buffer !== "undefined") {
      // Node.js environment (including tests)
      uint8Array = new Uint8Array(Buffer.from(compressedData, "base64"));
    } else {
      // Browser environment
      const binaryString = atob(compressedData);
      uint8Array = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i);
      }
    }

    // Decompress using fflate
    const decompressed = gunzipSync(uint8Array);

    // Convert back to string
    const text = new TextDecoder().decode(decompressed);

    return text;
  } catch (error) {
    console.error("Error decompressing string:", error);
    throw new Error("Failed to decompress data");
  }
};

/**
 * Check if content should be compressed based on size threshold
 * @param content - The content to check
 * @returns true if content should be compressed
 */
export const shouldCompress = (content: string | null | undefined): boolean => {
  if (!content) return false;

  // Calculate size in bytes (UTF-8 encoding)
  const sizeInBytes = new TextEncoder().encode(content).length;

  return sizeInBytes > COMPRESSION_THRESHOLD;
};

/**
 * Compress field if it exceeds the threshold
 * Returns an object indicating if compression was applied and the data
 * @param fieldValue - The field value to potentially compress
 * @returns Object with compressed flag and data
 */
export const compressFieldIfNeeded = (
  fieldValue: string | null | undefined
): {
  compressed: boolean;
  data: string | null | undefined;
} => {
  if (!fieldValue) {
    return { compressed: false, data: fieldValue };
  }

  if (shouldCompress(fieldValue)) {
    const compressed = compressString(fieldValue);
    return { compressed: true, data: `${COMPRESSION_MARKER}${compressed}` };
  }

  return { compressed: false, data: fieldValue };
};

/**
 * Check if a field value is compressed
 * @param fieldValue - The field value to check
 * @returns true if the value is marked as compressed
 */
export const isCompressed = (
  fieldValue: string | null | undefined
): boolean => {
  if (!fieldValue) return false;
  return fieldValue.startsWith(COMPRESSION_MARKER);
};

/**
 * Decompress a field value if it's compressed, otherwise return as-is
 * @param fieldValue - The field value to potentially decompress
 * @returns Original or decompressed string
 */
export const decompressFieldIfNeeded = (
  fieldValue: string | null | undefined
): string | null | undefined => {
  if (!fieldValue) return fieldValue;

  if (isCompressed(fieldValue)) {
    const compressedData = fieldValue.slice(COMPRESSION_MARKER.length);
    return decompressString(compressedData);
  }

  return fieldValue;
};

/**
 * Calculate compression ratio
 * @param original - Original string
 * @param compressed - Compressed string (with marker)
 * @returns Compression ratio (e.g., 0.3 means 30% of original size)
 */
export const getCompressionRatio = (
  original: string,
  compressed: string
): number => {
  const originalSize = new TextEncoder().encode(original).length;
  const compressedSize = new TextEncoder().encode(compressed).length;
  return compressedSize / originalSize;
};
